import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Send, Bot, User, Camera, ImageIcon, Heart, Briefcase, Activity, Palette, Star, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTION_CATEGORIES = [
  {
    id: 'basics',
    name: 'Les bases',
    icon: Sparkles,
    color: 'bg-gray-100 border-gray-300 text-gray-700',
    questions: [
      "Qu'est-ce que la chiromancie ?",
      "Comment bien positionner une main pour la lire ?",
      "Quelle main faut-il lire en priorité ?",
    ]
  },
  {
    id: 'lines',
    name: 'Les lignes',
    icon: Activity,
    color: 'bg-blue-50 border-blue-300 text-blue-700',
    questions: [
      "Quelles sont les lignes principales de la main ?",
      "Comment reconnaître la ligne de vie ?",
      "Que signifie une ligne de cœur longue ?",
    ]
  },
  {
    id: 'love',
    name: 'Amour 💕',
    icon: Heart,
    color: 'bg-pink-50 border-pink-300 text-pink-700',
    questions: [
      "Comment lire la ligne de cœur ?",
      "Où se trouve la ligne de mariage ?",
      "Comment interpréter les lignes d'affection ?",
    ]
  },
  {
    id: 'career',
    name: 'Carrière 💼',
    icon: Briefcase,
    color: 'bg-indigo-50 border-indigo-300 text-indigo-700',
    questions: [
      "Comment reconnaître la ligne de destin ?",
      "Que révèle la ligne de tête sur la carrière ?",
      "Comment identifier les signes de réussite ?",
    ]
  },
  {
    id: 'mounts',
    name: 'Les monts',
    icon: Star,
    color: 'bg-purple-50 border-purple-300 text-purple-700',
    questions: [
      "Quels sont les différents monts de la main ?",
      "Que signifie le mont de Vénus ?",
      "Comment interpréter le mont de Jupiter ?",
    ]
  },
  {
    id: 'signs',
    name: 'Signes spéciaux',
    icon: Palette,
    color: 'bg-orange-50 border-orange-300 text-orange-700',
    questions: [
      "Que signifient les croix sur la main ?",
      "Comment interpréter les étoiles ?",
      "Que révèlent les îles sur les lignes ?",
    ]
  },
];

const ChatGeneralPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('general');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    setMessages([
      {
        role: 'assistant',
        content: `Bonjour ! Je suis Aria, experte en chiromancie. 🔮\n\nCet espace est dédié à l'apprentissage de la chiromancie :\n• Comment lire les lignes de la main\n• Ce que chaque ligne, mont et signe révèle\n• Les secrets de cet art ancestral\n\n📚 Posez vos questions pour apprendre à décrypter les mains !\n\n✋ Pour une analyse personnalisée de VOTRE main, utilisez plutôt la fonction "Commencer ma lecture".`,
        timestamp: new Date(),
        image_url: null
      },
    ]);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/general`, {
        question: messageText,
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.answer,
        image_url: response.data.image_url,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
      
      const fallbackMessage = {
        role: 'assistant',
        content: "Je suis désolée, je rencontre un problème technique. Pour obtenir une analyse personnalisée de votre main, je vous invite à commencer une lecture !",
        timestamp: new Date(),
        image_url: null
      };
      setMessages((prev) => [...prev, fallbackMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedQuestion = (question) => {
    sendMessage(question);
  };

  const currentCategory = QUESTION_CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex flex-col">
      {/* Header */}
      <div className="bg-card border-b border-border p-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/')}
            className="rounded-full"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg">Aria - Expert Chiromancie</h1>
              <p className="text-xs text-muted-foreground">Apprenez à lire et comprendre les lignes de la main</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Messages + Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-3xl mx-auto space-y-4">
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.role === 'user'
                        ? 'bg-primary'
                        : 'bg-gradient-to-br from-orange-500 to-orange-600'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <User className="h-4 w-4 text-primary-foreground" />
                    ) : (
                      <Bot className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div
                    className={`flex-1 max-w-[85%] ${
                      message.role === 'user' ? 'text-right' : ''
                    }`}
                  >
                    <div
                      className={`inline-block p-4 rounded-2xl ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card border border-border'
                      }`}
                    >
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.content}
                      </p>
                      
                      {message.image_url && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="mt-4"
                        >
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                            <ImageIcon className="h-3 w-3" />
                            <span>Illustration</span>
                          </div>
                          <img
                            src={message.image_url}
                            alt="Illustration chiromancie"
                            className="rounded-xl max-w-full h-auto border border-border shadow-lg"
                            style={{ maxHeight: '300px' }}
                          />
                        </motion.div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 px-2">
                      {message.timestamp.toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-card border border-border p-4 rounded-2xl">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{animationDelay: '0.1s'}} />
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{animationDelay: '0.2s'}} />
                    </div>
                    <span className="text-xs text-muted-foreground ml-2">Aria réfléchit...</span>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Sidebar - Categories & Questions */}
        <div className="hidden lg:block w-80 border-l border-border bg-card/50 overflow-y-auto">
          {/* Category Tabs */}
          <div className="sticky top-0 bg-card/95 backdrop-blur-sm border-b border-border p-3">
            <h3 className="font-heading text-sm font-semibold mb-3">Catégories</h3>
            <div className="flex flex-wrap gap-1.5">
              {QUESTION_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-2.5 py-1.5 text-xs rounded-full border transition-all ${
                    activeCategory === category.id
                      ? category.color + ' font-semibold'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Questions for Selected Category */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              {currentCategory && <currentCategory.icon className="h-4 w-4 text-orange-500" />}
              <span className="text-xs font-semibold">{currentCategory?.name}</span>
            </div>
            <div className="space-y-2">
              {currentCategory?.questions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestedQuestion(question)}
                  disabled={isLoading}
                  className={`w-full px-3 py-2.5 text-xs rounded-xl border transition-colors text-left leading-relaxed disabled:opacity-50 disabled:cursor-not-allowed ${currentCategory.color} hover:opacity-80`}
                >
                  {question}
                </button>
              ))}
            </div>
            
            {/* CTA */}
            <div className="mt-6 p-4 bg-gradient-to-br from-orange-100 to-orange-50 rounded-xl border border-orange-200">
              <p className="text-xs text-orange-900 mb-3 font-medium">
                Envie d'analyser VOTRE main ?
              </p>
              <Button
                onClick={() => navigate('/scan')}
                size="sm"
                className="w-full rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs"
              >
                <Camera className="mr-2 h-3 w-3" />
                Analyser ma main
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Categories & Questions */}
      <div className="lg:hidden border-t border-border p-3 bg-orange-50">
        <div className="max-w-4xl mx-auto">
          {/* Category Pills */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-2 scrollbar-hide">
            {QUESTION_CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex-shrink-0 px-3 py-1.5 text-xs rounded-full border transition-all ${
                  activeCategory === category.id
                    ? category.color + ' font-semibold'
                    : 'bg-white border-gray-200 text-gray-600'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
          {/* Questions */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {currentCategory?.questions.map((question, index) => (
              <button
                key={index}
                onClick={() => handleSuggestedQuestion(question)}
                disabled={isLoading}
                className="flex-shrink-0 px-3 py-2 text-xs bg-white hover:bg-orange-100 disabled:opacity-50 rounded-full border border-orange-200 transition-colors whitespace-nowrap"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Input - Question Libre */}
      <div className="border-t-2 border-orange-300 p-4 bg-white shadow-lg">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-muted-foreground">💬 Question libre</span>
          </div>
          <div className="flex gap-3">
            <input
              data-testid="chat-input"
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Posez votre propre question sur la chiromancie..."
              className="flex-1 px-4 py-3 rounded-full border-2 border-orange-200 bg-background focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={isLoading}
            />
            <Button
              data-testid="send-button"
              onClick={() => sendMessage()}
              disabled={!inputMessage.trim() || isLoading}
              className="rounded-full w-12 h-12 p-0 bg-orange-500 hover:bg-orange-600"
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatGeneralPage;
